import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import { useDropzone } from 'react-dropzone';
import { useMsal } from '@azure/msal-react';
import { sendMailViaGraph, buildSRTEmailHTML } from '../../utils/graphMailService';

const API_URL = process.env.REACT_APP_CURRENT_URL || 'http://localhost:8080';

const steps = ['Video invoer', 'Instellingen', 'Genereren', 'Resultaat'];

function SRTGenerator() {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // Step 1: Video input
  const [videoFile, setVideoFile] = useState(null);
  const [frameIoLink, setFrameIoLink] = useState('');
  const [inputMethod, setInputMethod] = useState('upload'); // 'upload' or 'link'

  // Step 2: Settings
  const [language, setLanguage] = useState('nl');
  const [aspectRatio, setAspectRatio] = useState('16:9'); // '16:9', '9:16', '1:1'
  const [subtitleStyle, setSubtitleStyle] = useState('box'); // 'box' (zwarte achtergrond) or 'outline' (alleen outline)
  const [scriptFile, setScriptFile] = useState(null);
  const [scriptText, setScriptText] = useState('');
  const [email, setEmail] = useState(activeAccount?.username || '');
  const [sendEmail, setSendEmail] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [uploadToFrameIo, setUploadToFrameIo] = useState(false);
  const [frameioLink, setFrameioLink] = useState(null);

  // Step 4: Result
  const [generatedSRT, setGeneratedSRT] = useState(null);
  const [correctedSRT, setCorrectedSRT] = useState(null);
  const [fcpxml, setFcpxml] = useState(null);
  const [feedback, setFeedback] = useState('');

  // Video dropzone
  const onDropVideo = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setVideoFile(acceptedFiles[0]);
      setInputMethod('upload');
      setFrameIoLink('');
    }
  }, []);

  const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps, isDragActive: isVideoDragActive } = useDropzone({
    onDrop: onDropVideo,
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav']
    },
    maxFiles: 1,
    maxSize: 500 * 1024 * 1024 // 500MB
  });

  // Script dropzone
  const onDropScript = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setScriptFile(acceptedFiles[0]);
      // Read file content
      const reader = new FileReader();
      reader.onload = (e) => setScriptText(e.target.result);
      reader.readAsText(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps: getScriptRootProps, getInputProps: getScriptInputProps } = useDropzone({
    onDrop: onDropScript,
    accept: {
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1
  });

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate video input
      if (!videoFile && !frameIoLink) {
        setError('Upload een video of voer een Frame.io link in');
        return;
      }
    }
    if (activeStep === 1) {
      // Validate email
      if (!email || !email.includes('@')) {
        setError('Voer een geldig emailadres in');
        return;
      }
    }
    setError(null);
    
    if (activeStep === 2) {
      // Start generation
      handleGenerate();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setProgress(0);
    setProgressMessage('Voorbereiden...');
    setError(null);
    
    try {
      const formData = new FormData();
      
      if (videoFile) {
        formData.append('video', videoFile);
      } else if (frameIoLink) {
        formData.append('frameIoLink', frameIoLink);
      }
      
      formData.append('language', language);
      formData.append('aspectRatio', aspectRatio);
      formData.append('subtitleStyle', subtitleStyle);
      formData.append('email', email);
      formData.append('uploadToFrameIo', uploadToFrameIo.toString());
      
      // Send original filename for Frame.io
      if (videoFile) {
        formData.append('originalFilename', videoFile.name);
      }
      
      if (scriptText) {
        formData.append('script', scriptText);
      }

      setProgress(10);
      setProgressMessage('Video uploaden naar server...');
      
      console.log('Uploading to:', `${API_URL}/Api/srt-generator/generate`);

      const response = await fetch(`${API_URL}/Api/srt-generator/generate`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || `Server error: ${response.status}`;
        } catch {
          errorMessage = `Server error: ${response.status} - ${errorText.substring(0, 100)}`;
        }
        throw new Error(errorMessage);
      }

      // Poll for progress
      const result = await response.json();
      console.log('Job created:', result);
      
      if (!result.jobId) {
        throw new Error('Geen job ID ontvangen van server');
      }
      
      await pollProgress(result.jobId);

    } catch (err) {
      console.error('Generate error:', err);
      setError(err.message || 'Onbekende fout bij uploaden');
      setLoading(false);
    }
  };

  const pollProgress = async (jobId) => {
    console.log('Starting to poll job:', jobId);
    let errorCount = 0;
    const maxErrors = 5; // Allow up to 5 consecutive errors before giving up
    
    const pollInterval = setInterval(async () => {
      try {
        const statusUrl = `${API_URL}/Api/srt-generator/status/${jobId}`;
        console.log('Polling:', statusUrl);
        
        const response = await fetch(statusUrl);
        
        if (!response.ok) {
          throw new Error(`Status check failed: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Job status:', data);
        
        // Reset error count on successful poll
        errorCount = 0;
        
        setProgress(data.progress || 0);
        setProgressMessage(data.message || 'Verwerken...');

        if (data.status === 'completed') {
          clearInterval(pollInterval);
          setGeneratedSRT(data.originalSRT);
          setCorrectedSRT(data.correctedSRT);
          setFcpxml(data.fcpxml || null);
          setFrameioLink(data.frameio?.link || null);
          
          // Check if Frame.io upload was requested but failed
          if (uploadToFrameIo && data.frameio && !data.frameio.success && data.frameio.error) {
            console.warn('Frame.io upload failed:', data.frameio.error);
            setError(`SRT gegenereerd, maar Frame.io upload mislukt: ${data.frameio.error}`);
          }
          
          // Send email via Graph API if enabled
          if (sendEmail && email && data.originalSRT && data.correctedSRT) {
            setProgressMessage('Email versturen...');
            try {
              // Build attachments array
              const attachments = [
                { filename: 'ondertiteling_origineel.srt', content: data.originalSRT },
                { filename: 'ondertiteling_gecorrigeerd.srt', content: data.correctedSRT }
              ];
              
              // Add FCP-XML if available
              if (data.fcpxml) {
                attachments.push({ filename: 'ondertiteling.fcpxml', content: data.fcpxml });
              }
              
              await sendMailViaGraph(instance, activeAccount, {
                to: email,
                subject: '🎬 Je SRT ondertiteling is klaar!',
                body: buildSRTEmailHTML({
                  feedback: data.feedback,
                  frameioLink: data.frameio?.link,
                  aspectRatio: data.aspectRatio,
                  hasFcpxml: !!data.fcpxml
                }),
                attachments
              });
              setEmailSent(true);
            } catch (emailError) {
              console.error('Email verzenden mislukt:', emailError);
              // Don't fail the whole process, just note it
              setError(`SRT gegenereerd, maar email versturen mislukt: ${emailError.message}`);
            }
          }
          setFeedback(data.feedback);
          setLoading(false);
          setActiveStep(3);
        } else if (data.status === 'error') {
          clearInterval(pollInterval);
          setError(data.error || 'Er ging iets mis bij de verwerking');
          setLoading(false);
        }
      } catch (err) {
        errorCount++;
        console.warn(`Poll error ${errorCount}/${maxErrors}:`, err.message);
        
        if (errorCount >= maxErrors) {
          clearInterval(pollInterval);
          setError('Kon status niet ophalen na meerdere pogingen. Probeer opnieuw.');
          setLoading(false);
        }
        // Otherwise, keep polling and hope it recovers
      }
    }, 2000);
  };

  const handleDownload = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setActiveStep(0);
    setVideoFile(null);
    setFrameIoLink('');
    setScriptFile(null);
    setScriptText('');
    setEmail(activeAccount?.username || '');
    setGeneratedSRT(null);
    setCorrectedSRT(null);
    setFcpxml(null);
    setFrameioLink(null);
    setFeedback('');
    setError(null);
    setEmailSent(false);
    setSendEmail(true);
    setSubtitleStyle('box');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              📹 Video of audio uploaden
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Chip 
                label="Upload bestand" 
                color={inputMethod === 'upload' ? 'primary' : 'default'}
                onClick={() => setInputMethod('upload')}
                icon={<CloudUploadIcon />}
              />
              <Chip 
                label="Frame.io link" 
                color={inputMethod === 'link' ? 'primary' : 'default'}
                onClick={() => setInputMethod('link')}
                icon={<LinkIcon />}
              />
            </Box>

            {inputMethod === 'upload' ? (
              <Paper
                {...getVideoRootProps()}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: isVideoDragActive ? '#e3f2fd' : videoFile ? '#e8f5e9' : '#fafafa',
                  border: '2px dashed',
                  borderColor: isVideoDragActive ? '#2196f3' : videoFile ? '#4caf50' : '#ccc',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#2196f3',
                    backgroundColor: '#e3f2fd'
                  }
                }}
              >
                <input {...getVideoInputProps()} />
                {videoFile ? (
                  <Box>
                    <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                    <Typography variant="h6">{videoFile.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                    <Tooltip title="Verwijder bestand">
                      <IconButton 
                        onClick={(e) => { e.stopPropagation(); setVideoFile(null); }}
                        sx={{ mt: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                    <Typography variant="h6">
                      {isVideoDragActive ? 'Drop hier!' : 'Sleep video/audio hierheen'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      of klik om te selecteren (MP4, MOV, MP3, WAV - max 500MB)
                    </Typography>
                  </Box>
                )}
              </Paper>
            ) : (
              <TextField
                fullWidth
                label="Frame.io link"
                placeholder="https://app.frame.io/..."
                value={frameIoLink}
                onChange={(e) => {
                  setFrameIoLink(e.target.value);
                  setVideoFile(null);
                }}
                InputProps={{
                  startAdornment: <LinkIcon sx={{ mr: 1, color: '#999' }} />
                }}
              />
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              ⚙️ Instellingen
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Taal van de video</InputLabel>
              <Select
                value={language}
                label="Taal van de video"
                onChange={(e) => setLanguage(e.target.value)}
              >
                <MenuItem value="nl">🇳🇱 Nederlands</MenuItem>
                <MenuItem value="en">🇬🇧 Engels</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Video formaat (voor FCP-XML)</InputLabel>
              <Select
                value={aspectRatio}
                label="Video formaat (voor FCP-XML)"
                onChange={(e) => setAspectRatio(e.target.value)}
              >
                <MenuItem value="16:9">📺 16:9 Landscape (1920×1080)</MenuItem>
                <MenuItem value="9:16">📱 9:16 Portrait / Reels (1080×1920)</MenuItem>
                <MenuItem value="1:1">⬜ 1:1 Vierkant (1080×1080)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Ondertiteling stijl (burned-in)</InputLabel>
              <Select
                value={subtitleStyle}
                label="Ondertiteling stijl (burned-in)"
                onChange={(e) => setSubtitleStyle(e.target.value)}
              >
                <MenuItem value="box">📦 Zwarte achtergrond + witte tekst</MenuItem>
                <MenuItem value="outline">✏️ Witte tekst met zwarte outline (standaard)</MenuItem>
              </Select>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>
              📝 Script of voice-over tekst (optioneel)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Upload een script om de nauwkeurigheid van de transcriptie te verbeteren
            </Typography>

            <Paper
              {...getScriptRootProps()}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: scriptFile ? '#e8f5e9' : '#fafafa',
                border: '1px dashed #ccc',
                borderRadius: 2,
                mb: 2,
                '&:hover': { borderColor: '#2196f3' }
              }}
            >
              <input {...getScriptInputProps()} />
              {scriptFile ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <DescriptionIcon color="success" />
                  <Typography>{scriptFile.name}</Typography>
                  <IconButton 
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setScriptFile(null); setScriptText(''); }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Sleep script hier of klik (TXT, DOC, DOCX)
                </Typography>
              )}
            </Paper>

            <Typography variant="body2" sx={{ mb: 1 }}>Of plak de tekst:</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Plak hier je script of voice-over tekst..."
              value={scriptText}
              onChange={(e) => setScriptText(e.target.value)}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>
              📧 Email voor resultaat
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={sendEmail} 
                  onChange={(e) => setSendEmail(e.target.checked)}
                  color="primary"
                />
              }
              label="📧 Verstuur resultaat per email"
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox 
                  checked={uploadToFrameIo} 
                  onChange={(e) => setUploadToFrameIo(e.target.checked)}
                  color="secondary"
                />
              }
              label="🎬 Upload naar Frame.io (met burned-in ondertitels)"
              sx={{ mb: 2 }}
            />
            
            {sendEmail && (
              <>
                <TextField
                  fullWidth
                  type="email"
                  label="Emailadres"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: '#999' }} />
                  }}
                  sx={{ mb: 1 }}
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <SendIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Email wordt verstuurd via je Microsoft account ({activeAccount?.username})
                  </Typography>
                </Alert>
              </>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              🚀 Klaar om te genereren
            </Typography>

            <Card sx={{ maxWidth: 500, mx: 'auto', mt: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">Samenvatting</Typography>
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ textAlign: 'left' }}>
                  <Typography><strong>Video:</strong> {videoFile?.name || frameIoLink || '-'}</Typography>
                  <Typography><strong>Taal:</strong> {language === 'nl' ? '🇳🇱 Nederlands' : '🇬🇧 Engels'}</Typography>
                  <Typography><strong>Formaat:</strong> {aspectRatio === '16:9' ? '📺 16:9 Landscape' : aspectRatio === '9:16' ? '📱 9:16 Portrait' : '⬜ 1:1 Vierkant'}</Typography>
                  <Typography><strong>Ondertiteling:</strong> {subtitleStyle === 'box' ? '📦 Zwarte achtergrond' : '✏️ Witte tekst met outline'}</Typography>
                  <Typography><strong>Script:</strong> {scriptText ? 'Ja (' + scriptText.length + ' karakters)' : 'Nee'}</Typography>
                  <Typography><strong>Email:</strong> {sendEmail ? email : 'Nee'}</Typography>
                  <Typography><strong>Frame.io:</strong> {uploadToFrameIo ? '✅ Upload met ondertitels' : 'Nee'}</Typography>
                </Box>
              </CardContent>
            </Card>

            {loading && (
              <Box sx={{ mt: 4 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>{progressMessage}</Typography>
                <LinearProgress variant="determinate" value={progress} sx={{ mt: 2, maxWidth: 400, mx: 'auto' }} />
                <Typography variant="body2" color="textSecondary">{progress}%</Typography>
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 3 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6">✅ SRT succesvol gegenereerd!</Typography>
              {emailSent && (
                <Typography variant="body2">📧 Email verstuurd naar {email}</Typography>
              )}
              {sendEmail && !emailSent && (
                <Typography variant="body2" color="warning.main">⚠️ Email kon niet worden verstuurd</Typography>
              )}
            </Alert>

            {feedback && (
              <Card sx={{ mb: 3, backgroundColor: '#fff3e0' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    💡 Feedback van AI-controle:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {feedback}
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {generatedSRT && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(generatedSRT, 'ondertiteling_origineel.srt')}
                >
                  Download originele SRT
                </Button>
              )}
              {correctedSRT && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(correctedSRT, 'ondertiteling_gecorrigeerd.srt')}
                >
                  Download gecorrigeerde SRT
                </Button>
              )}
              {fcpxml && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(fcpxml, 'ondertiteling.fcpxml')}
                >
                  Download FCP-XML
                </Button>
              )}
            </Box>

            {frameioLink && (
              <Card sx={{ mt: 3, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🎬 Frame.io Preview
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Je video met ondertitels is geüpload naar Frame.io voor review:
                  </Typography>
                  <Button
                    variant="contained"
                    color="info"
                    href={frameioLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in Frame.io →
                  </Button>
                </CardContent>
              </Card>
            )}

            <Box sx={{ mt: 4 }}>
              <Button variant="outlined" onClick={handleReset}>
                Nieuwe SRT genereren
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      maxWidth: 800, 
      mx: 'auto', 
      p: 3,
      minHeight: 'calc(100vh - 100px)'
    }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🎬 SRT Generator
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
          Genereer automatisch ondertitels uit video of audio met behulp van AI
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}

        {activeStep < 3 && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Vorige
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
            >
              {activeStep === 2 ? 'Genereer SRT' : 'Volgende'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default SRTGenerator;

